import { useState } from "react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/perms";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Input } from "@/pages/admin/ManagePages";
import { Notice } from "@/components/form/FormFeedback";
import { usePageTitle } from "@/components/layout/AppLayout";
import { PasswordStrength } from "@/components/form/PasswordStrength";

const Info = ({ label, value, testId }) => (
    <div><dt className="edi-eyebrow">{label}</dt><dd className="mt-1 font-semibold break-words" data-testid={testId}>{value || "—"}</dd></div>
);

const PasswordForm = () => {
    const { user } = useAuth();
    const empty = { current_password: "", password: "", password_confirm: "" };
    const [v, setV] = useState(empty);
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState(null);
    const submit = async (e) => {
        e.preventDefault(); setMsg(null);
        try { const { data } = await api.put("/account/password", v); setV(empty); setErrors({}); setMsg({ kind: "success", text: data.message }); }
        catch (err) { const f = fieldErrors(err); setErrors(f || {}); if (!f) setMsg({ kind: "error", text: errorMessage(err) }); }
    };
    return (
        <form noValidate onSubmit={submit} className="edi-card p-5 space-y-4" data-testid="account-password-form">
            <h2 className="text-xl font-bold">Cambia password</h2>
            <Input id="acc-current" type="password" label="Password attuale" autoComplete="current-password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} error={errors.current_password} />
            <Input id="acc-new" type="password" label="Nuova password" autoComplete="new-password" value={v.password} onChange={(e) => setV({ ...v, password: e.target.value })} error={errors.password} />
            <PasswordStrength id="acc-new-strength" password={v.password} email={user.email} />
            <Input id="acc-confirm" type="password" label="Conferma nuova password" autoComplete="new-password" value={v.password_confirm} onChange={(e) => setV({ ...v, password_confirm: e.target.value })} error={errors.password_confirm} />
            <button type="submit" className="btn-primary" data-testid="account-password-save">Aggiorna password</button>
            {msg && <Notice kind={msg.kind} role={msg.kind === "error" ? "alert" : "status"} testId="account-password-message">{msg.text}</Notice>}
        </form>
    );
};

const EmailForm = () => {
    const { user, refresh } = useAuth();
    const [email, setEmail] = useState(user.email);
    const [error, setError] = useState("");
    const [msg, setMsg] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setMsg("");
        try { const { data } = await api.put("/account/email", { email }); setError(""); setMsg(data.message); refresh(); }
        catch (err) { setError(fieldErrors(err)?.email || errorMessage(err)); }
    };
    return (
        <form noValidate onSubmit={submit} className="edi-card p-5 space-y-4" data-testid="account-email-form">
            <h2 className="text-xl font-bold">Cambia email di accesso</h2>
            <Input id="acc-email" type="email" label="Email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} error={error} />
            <button type="submit" className="btn-primary" data-testid="account-email-save">Salva email</button>
            <p role="status" aria-live="polite" data-testid="account-email-message">{msg}</p>
        </form>
    );
};

export const MioAccount = () => {
    usePageTitle("Il mio account");
    const { user } = useAuth();
    return (
        <>
            <PageHead title="Il mio account" />
            <dl className="edi-card p-5 grid gap-4 sm:grid-cols-2 mb-6" data-testid="account-info">
                <Info label="Nome" value={`${user.first_name || ""} ${user.last_name || ""}`.trim()} testId="account-name" />
                <Info label="Email" value={user.email} testId="account-email" />
                <Info label="Ruolo" value={ROLE_LABELS[user.role]} testId="account-role" />
                <Info label="Sede" value={user.role === "admin_sede" ? user.spin?.name : "Tutte le sedi"} testId="account-sede" />
            </dl>
            <div className="grid gap-6 lg:grid-cols-2 items-start">
                <PasswordForm />
                {user.role === "system_admin" && <EmailForm />}
            </div>
        </>
    );
};
